import React from 'react';
import { injectStripe, ReactStripeElements } from 'react-stripe-elements';
// @ts-ignore
import AdminUpgradePageHeader from 'admin/components/AdminUpgradePageHeader';
import Avatar from 'components/Avatar';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import CoachingDescription from './CoachingDescription';
import CoachingSuccessPage from './CoachingSuccessPage';
import CoachingConstants from '../../CoachingConstants';
import CurrentUser from 'auth/CurrentUser';
import EventTracking from 'lib/EventTracking';
import LinkConstants from 'constants/LinkConstants';
import LoadingIndicator from 'components/LoadingIndicator';
import MediaObject from 'components/MediaObject';
import PurchaseDescription from './PurchaseDescription';
import QuoteBox from 'components/QuoteBox';
import renderErrorReporter from '../../CoachingErrorReporter';
import Row from 'components/Row';
import SequentialProgressFooter from 'components/SequentialProgressFooter';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';

import { PurchaseCoachingProduct } from '../../mutations/__generated__/PurchaseCoachingProduct';
import { CoachingStudentsAndProduct_coaching_studentFamilyMembers as Student } from '../../queries/__generated__/CoachingStudentsAndProduct';
import { MutationResult } from 'react-apollo';

import '../styles/CoachingView.scss';

type Props = {
  isLoading: boolean;
  students: Student[] | undefined | null;
  productVariantUuid: string | undefined | null;
  onSubmit: (
    productVariantUuid: string,
    tokenizedCard: string,
    purchasedForUserUuid: string,
    email: string,
    name: string
  ) => void;
  purchaseResult: MutationResult<PurchaseCoachingProduct>;
};

type State = {
  currentStep: number;
  selectedChildName: null | string;
  cardInputCompleted: boolean;
};

class CoachingView extends React.Component<Props & ReactStripeElements.InjectedStripeProps, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      currentStep: 1,
      selectedChildName: null,
      cardInputCompleted: false,
    };
  }

  renderHeader() {
    /* the header, and so the ability to exit, is rendered in every view of this experience
       the tracking key array is meant to mirror the view the user is looking at so we can
       distinguish between the exit events for each */
    const onExitEventTrackingKeys = [
      'coaching.select_student.exit.click',
      'coaching.payment.exit.click',
      'coaching.confirmation.exit.click',
    ];
    const currentEventTrackingKey = onExitEventTrackingKeys[this.state.currentStep - 1];
    return (
      <AdminUpgradePageHeader
        title="Remind Coaching"
        startUrl={LinkConstants.COACHING}
        remindIconOnClickEventKey={currentEventTrackingKey}
        hideHelp
        hideClose
        useHref
      />
    );
  }

  renderLeftColumnContent(isLoading: boolean, students: Student[] | null | undefined) {
    if (isLoading) {
      return <LoadingIndicator />;
    } else if (!students) {
      return renderErrorReporter('Finished loading but students is undefined');
    } else {
      return this.state.currentStep === 1 ? (
        <CoachingDescription
          onChildSelection={event => this.setState({ selectedChildName: event.currentTarget.name })}
          selectedChildName={this.state.selectedChildName}
          isLoading={isLoading}
          students={students}
        />
      ) : (
        <PurchaseDescription
          selectedChildName={this.state.selectedChildName}
          onCardInputCompleted={() => this.setState({ cardInputCompleted: true })}
        />
      );
    }
  }

  renderRightColumnContent() {
    const quoteProps =
      CoachingConstants.TESTIMONIALS[
        this.state.currentStep - 1
      ]; /* currentStep starts at 1, not 0 */
    return (
      <Row size="large">
        <QuoteBox {...quoteProps} />
        <Box size="medium">
          <BoxBody size="small">
            <MediaObject gutter="small" center>
              <Avatar color="coachingBlue" glyphName="remind-icon" size="xlarge" />
              <Text align="left">Any questions? Contact us at coaching@remind.com</Text>
            </MediaObject>
          </BoxBody>
        </Box>
      </Row>
    );
  }

  studentNameToUuidMapping(students: Student[]): Record<string, string> {
    return students.reduce(
      (acc, student: Student) => {
        acc[student.name] = student.uuid;
        return acc;
      },
      {} as Record<string, string>
    );
  }

  async onSubmit() {
    EventTracking.trackKey('coaching.payment.submit.click');
    if (this.state.selectedChildName && this.props.stripe && this.props.students) {
      const user = CurrentUser.getUser();
      const { token, error } = await this.props.stripe.createToken({
        name: user.getDisplayName(),
      });
      const selectedStudentUuid = this.studentNameToUuidMapping(this.props.students)[
        this.state.selectedChildName
      ];
      if (error) {
        ToastActions.showErrorToast('There was a problem saving your card. Please try again.');
        console.error('Problem tokenizing card');
      }
      if (token && this.props.productVariantUuid) {
        this.props.onSubmit(
          this.props.productVariantUuid,
          token.id,
          selectedStudentUuid,
          user.getEmail(),
          user.getDisplayName()
        );
      } else {
        ToastActions.showErrorToast(
          'There was a problem completing your purchase. Please try again.'
        );
        console.error('Missing token or product variant uuid');
      }
    }
  }

  shouldShowSuccess() {
    return (
      this.state.currentStep === 3 &&
      this.props.purchaseResult.data &&
      this.props.purchaseResult.data.purchaseCoachingProductWithTokenizedCard.success
    );
  }

  renderFooter() {
    if (this.state.currentStep > 2) {
      return null;
    } else {
      const maxSteps = 2;
      return (
        <SequentialProgressFooter
          className="CoachingFooter"
          previous={this.state.currentStep > 1}
          currentStep={this.state.currentStep}
          steps={maxSteps}
          onNext={() => {
            if (this.state.currentStep === 2) {
              this.onSubmit();
            } else {
              EventTracking.trackKey('coaching.select_student.next.click');
            }
            this.setState(prevState => {
              return { currentStep: prevState.currentStep + 1 };
            });
          }}
          onPrevious={() => {
            EventTracking.trackKey('coaching.payment.back.click');
            this.setState(prevState => {
              return { currentStep: prevState.currentStep - 1 };
            });
          }}
          nextDisabled={
            this.state.selectedChildName === null ||
            this.state.currentStep > maxSteps ||
            this.props.purchaseResult.called ||
            (this.state.currentStep === 2 && !this.state.cardInputCompleted)
          }
          nextButtonText={this.state.currentStep === maxSteps ? 'Submit' : 'Next'}
        />
      );
    }
  }

  renderMainContent() {
    if (this.shouldShowSuccess()) {
      return <CoachingSuccessPage />;
    } else {
      return (
        <React.Fragment>
          <div className="CoachingView-content">
            <div className="CoachingView-content-body">
              <div className="CoachingView-content-body-forms">
                {this.renderLeftColumnContent(
                  this.props.isLoading || this.props.purchaseResult.loading,
                  this.props.students
                )}
              </div>
              <div className="CoachingView-content-body--desktop-side">
                {this.renderRightColumnContent()}
              </div>
            </div>
          </div>
          <div className="CoachingView-footer">{this.renderFooter()}</div>
        </React.Fragment>
      );
    }
  }

  render() {
    return (
      <div className="CoachingView">
        {this.renderHeader()}
        {this.renderMainContent()}
      </div>
    );
  }
}

export default injectStripe(CoachingView);
